import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Alert
} from 'react-bootstrap';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  MapPin,
  ExternalLink,
  Info,
  Zap,
  Droplet,
  Toilet,
  ShowerHead,
  Flame,
  Car,
  TreePine,
  User,
  Phone,
  Mail,
  X as XIcon
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { db } from '../firebase/firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'leaflet/dist/leaflet.css';

// Standard-Look für Leaflet Marker
L.Marker.prototype.options.icon = L.icon({
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Initialer Zustand für einen Lagerplatz
const initialPlatz = {
  name: '',
  standort: { lat: 0, lng: 0 },
  umgebung: [],
  vermieter: { vorname: '', nachname: '', email: '', telefon: '' },
  umgebungText: '',
  googleMapsLink: '',
  ort: '',
  plz: '',
  kanton: '',
  website: '',
};

// Statische CSS-Styles außerhalb der Komponente
const customStyles = `
  body {
    background-color: rgb(243, 242, 241) !important;
    padding-bottom: 2rem;
  }
  .hover-shadow {
    transition: all 0.3s ease !important;
  }
  .hover-shadow:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
  }
  /* Mobile Header */
  .mobile-header {
    background-color: #F3F2F1;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding: 1rem;
    margin-bottom: 1rem;
  }
  .mobile-header h2 {
    font-weight: bold;
    margin: 0;
    font-size: 2rem;
    color: #333;
  }
  .mobile-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .mobile-search-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }
  .mobile-search-container {
    position: absolute;
    right: 100%;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    background: #f8f9fa;
    border-radius: 20px;
    margin-right: 8px;
  }
  .mobile-search-container.expanded {
    width: 200px;
    opacity: 1;
    transform: translateX(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  .mobile-search-container.collapsed {
    width: 0;
    opacity: 0;
    transform: translateX(20px);
    pointer-events: none;
  }
  .mobile-search-input {
    border: none;
    padding: 0.5rem 1rem;
    width: 100%;
    background: transparent;
    font-size: 0.95rem;
    color: #333;
  }
  .mobile-search-input:focus {
    outline: none;
  }
  .btn-icon {
    background: none;
    border: none;
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 20px;
    color: #333;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    position: relative;
    z-index: 2;
  }
  .btn-icon:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: #0078d4;
  }
  .btn-icon.active {
    background-color: #f0f0f0;
    color: #0078d4;
  }
  .btn-new-lager {
    background-color: #333;
    color: #fff;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 6px;
    font-size: 1rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
  }
  .btn-new-lager:hover {
    background-color: #222;
  }
  .custom-search-input .input-group-text {
    border-radius: 6px 0 0 6px;
    border: 1px solid #ced4da;
    background-color: #e9ecef;
  }
  .custom-search-input .form-control {
    border: 1px solid #ced4da;
    border-left: none;
    border-radius: 0 6px 6px 0;
    padding: 0.5rem 1rem;
  }
  @media (max-width: 767px) {
    .row-cols-2 > * {
      flex: 0 0 50%;
      max-width: 50%;
    }
  }
`;

// Mapping für Umgebung-Icons
const environmentIcons = {
  'Strom': { icon: <Zap size={14} />, color: '#fbbf24' },
  'Trinkwasser vor Ort': { icon: <Droplet size={14} />, color: '#38bdf8' },
  'WC': { icon: <Toilet size={14} />, color: '#10b981' },
  'Dusche': { icon: <ShowerHead size={14} />, color: '#6366f1' },
  'Feuerstelle': { icon: <Flame size={14} />, color: '#ef4444' },
  'Parkplatz': { icon: <Car size={14} />, color: '#64748b' },
  'Wiese': { icon: <TreePine size={14} />, color: '#22c55e' }
};

// Ausgelagerte, memoized Komponente für einen Lagerplatz
const LagerCard = React.memo(({ platz, onEdit, onDelete }) => (
  <Col className="d-flex justify-content-center">
    <Card className="h-100 shadow-sm hover-shadow lager-card" style={{ width: '380px' }}>
      <Card.Body className="d-flex flex-column p-0">
        {/* Map Container – Höhe reduziert */}
        <div className="map-container" style={{ height: '180px', width: '100%' }}>
          <MapContainer
            center={[platz.standort.lat, platz.standort.lng]}
            zoom={13}
            scrollWheelZoom={false}
            style={{
              height: '100%',
              width: '100%',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              overflow: 'hidden'
            }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            <Marker position={[platz.standort.lat, platz.standort.lng]}>
              <Popup>
                <strong>{platz.name}</strong>
                <br />
                {platz.plz} {platz.ort}
              </Popup>
            </Marker>
          </MapContainer>
        </div>
        {/* Header Section: Icon + Titel und Aktionen */}
        <div className="p-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center">
              <div
                className="me-2 d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '8px',
                  borderRadius: '10px',
                  width: '38px',
                  height: '38px',
                  flexShrink: 0,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}
              >
                <MapPin size={20} style={{ color: '#0369a1' }} strokeWidth={1.5} />
              </div>
              <h3 style={{ fontWeight: '600', color: '#334155', fontSize: '1.1rem', margin: 0, lineHeight: '1.3' }}>
                {platz.name}
              </h3>
            </div>
            <div className="d-flex gap-1">
              <Button
                variant="link"
                size="sm"
                onClick={() => onEdit(platz)}
                className="p-1 d-flex align-items-center justify-content-center"
                style={{
                  color: '#64748b',
                  width: '28px',
                  height: '28px',
                  minWidth: '28px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)'
                }}
                title="Bearbeiten"
              >
                <Edit size={14} />
              </Button>
              <Button
                variant="link"
                size="sm"
                onClick={() => onDelete(platz.id)}
                className="p-1 d-flex align-items-center justify-content-center"
                style={{
                  color: '#64748b',
                  width: '28px',
                  height: '28px',
                  minWidth: '28px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)'
                }}
                title="Löschen"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        </div>
        {/* Content Section */}
        <div className="p-2">
          <div className="mb-2">
            <div
              style={{
                fontSize: '0.85rem',
                color: '#475569',
                backgroundColor: '#f8fafc',
                padding: '4px 10px',
                borderRadius: '100px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                border: '1px solid #f1f5f9'
              }}
            >
              <span>Ort:</span>
              <span style={{ color: '#0369a1', fontWeight: '500' }}>
                {platz.plz} {platz.ort}, {platz.kanton}
              </span>
            </div>
          </div>
          <div className="mb-2">
            <a
              href={platz.googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="d-inline-flex align-items-center gap-2"
              style={{ fontSize: '0.85rem', color: '#0369a1', textDecoration: 'none' }}
            >
              <ExternalLink size={14} />
              In Google Maps öffnen
            </a>
          </div>
          {platz.umgebung && platz.umgebung.length > 0 && (
            <div className="mb-2">
              <div style={{ fontSize: '0.85rem', color: '#475569', marginBottom: '0.5rem', fontWeight: '600' }}>
                Umgebung:
              </div>
              <div className="d-flex flex-wrap gap-2">
                {platz.umgebung.map((item, index) => {
                  const { icon, color } = environmentIcons[item] || { icon: null, color: '#64748b' };
                  return (
                    <span
                      key={index}
                      style={{
                        backgroundColor: `${color}10`,
                        color: color,
                        padding: '4px 8px',
                        borderRadius: '100px',
                        fontSize: '0.75rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: `1px solid ${color}20`,
                        fontWeight: '500'
                      }}
                    >
                      {icon}
                      {item}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mt-2">
            <div
              style={{
                fontSize: '0.85rem',
                color: '#475569',
                backgroundColor: '#f8fafc',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #f1f5f9'
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-1">
                <Info size={16} style={{ color: '#0f172a' }} />
                <span style={{ fontWeight: '500', color: '#0f172a' }}>Vermieter</span>
              </div>
              <div className="d-flex flex-column gap-1">
                <div className="d-flex align-items-center gap-2">
                  <User size={14} style={{ color: '#64748b' }} />
                  <span>{platz.vermieter.vorname} {platz.vermieter.nachname}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Mail size={14} style={{ color: '#64748b' }} />
                  <span>{platz.vermieter.email}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Phone size={14} style={{ color: '#64748b' }} />
                  <span>{platz.vermieter.telefon}</span>
                </div>
              </div>
              {platz.website && (
                <div className="mt-2">
                  <a
                    href={platz.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#0369a1', textDecoration: 'none', fontSize: '0.8rem' }}
                  >
                    <ExternalLink size={14} className="me-1" />
                    Website besuchen
                  </a>
                </div>
              )}
            </div>
          </div>
          {platz.umgebungText && (
            <div className="mt-2">
              <div style={{ fontSize: '0.85rem', color: '#475569', marginBottom: '0.5rem' }}>
                Zusätzliche Informationen:
              </div>
              <div
                style={{
                  backgroundColor: '#f8fafc',
                  color: '#0f172a',
                  padding: '6px 10px',
                  borderRadius: '8px',
                  border: '1px solid #f1f5f9',
                  fontSize: '0.85rem'
                }}
              >
                {platz.umgebungText}
              </div>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  </Col>
));

function Lagerplatzverzeichnis() {
  const [lagerplaetze, setLagerplaetze] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentPlatz, setCurrentPlatz] = useState(initialPlatz);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSearch, setShowSearch] = useState(false);

  // Laden der Lagerplätze
  useEffect(() => {
    const fetchLagerplaetze = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getDocs(collection(db, 'lagerplaetze'));
        setLagerplaetze(data.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id })));
      } catch (err) {
        console.error('Fehler beim Abrufen der Lagerplätze:', err);
        setError('Lagerplätze konnten nicht geladen werden');
        toast.error('Fehler beim Laden der Lagerplätze');
      } finally {
        setIsLoading(false);
      }
    };
    fetchLagerplaetze();
  }, []);

  const handleShowModal = useCallback(() => setShowModal(true), []);
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setIsEditing(false);
    setCurrentPlatz(initialPlatz);
  }, []);

  const handleSearchChange = useCallback((e) => setSearchTerm(e.target.value), []);

  const handleGoogleMapsLinkChange = useCallback((e) => {
    const newLink = e.target.value;
    setCurrentPlatz((prev) => ({ ...prev, googleMapsLink: newLink }));
    if (newLink) {
      extractCoordinatesFromLink(newLink);
    }
  }, []);

  const handleTextFieldChange = useCallback((e, fieldName) => {
    setCurrentPlatz((prev) => ({ ...prev, [fieldName]: e.target.value }));
  }, []);

  const handleVermieterFieldChange = useCallback((e, fieldName) => {
    setCurrentPlatz((prev) => ({
      ...prev,
      vermieter: { ...prev.vermieter, [fieldName]: e.target.value },
    }));
  }, []);

  const handleCheckboxChange = useCallback((e) => {
    const { value, checked } = e.target;
    setCurrentPlatz((prevPlatz) => ({
      ...prevPlatz,
      umgebung: checked
        ? [...prevPlatz.umgebung, value]
        : prevPlatz.umgebung.filter((item) => item !== value),
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!currentPlatz.name || !currentPlatz.standort.lat || !currentPlatz.standort.lng) {
      toast.error('Bitte fülle alle erforderlichen Felder aus');
      return;
    }
    try {
      if (isEditing) {
        const platzDoc = doc(db, 'lagerplaetze', currentPlatz.id);
        await updateDoc(platzDoc, currentPlatz);
        setLagerplaetze((prev) =>
          prev.map((platz) => (platz.id === currentPlatz.id ? currentPlatz : platz))
        );
        toast.success('Lagerplatz erfolgreich aktualisiert');
      } else {
        const docRef = await addDoc(collection(db, 'lagerplaetze'), currentPlatz);
        setLagerplaetze((prev) => [...prev, { ...currentPlatz, id: docRef.id }]);
        toast.success('Lagerplatz erfolgreich erstellt');
      }
      handleCloseModal();
    } catch (err) {
      console.error('Fehler beim Speichern des Lagerplatzes:', err);
      toast.error('Fehler: Der Lagerplatz konnte nicht gespeichert werden');
    }
  }, [currentPlatz, isEditing, handleCloseModal]);

  const handleEdit = useCallback((platz) => {
    setCurrentPlatz(platz);
    setIsEditing(true);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm('Möchtest du diesen Lagerplatz wirklich löschen?')) {
      try {
        const platzDoc = doc(db, 'lagerplaetze', id);
        await deleteDoc(platzDoc);
        setLagerplaetze((prev) => prev.filter((platz) => platz.id !== id));
        toast.success('Lagerplatz erfolgreich gelöscht');
      } catch (err) {
        console.error('Fehler beim Löschen des Lagerplatzes:', err);
        toast.error('Fehler beim Löschen des Lagerplatzes');
      }
    }
  }, []);

  const extractCoordinatesFromLink = useCallback((link) => {
    const regex = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
    const match = link.match(regex);
    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      setCurrentPlatz((prevPlatz) => ({
        ...prevPlatz,
        standort: { lat, lng },
      }));
      geocodeCoordinates(lat, lng);
    } else if (link) {
      toast.warning('Ungültiger Google Maps Link. Bitte überprüfe den Link');
    }
  }, []);

  const geocodeCoordinates = useCallback(async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
      );
      if (!response.ok) {
        throw new Error('Fehler beim Abrufen der Geodaten');
      }
      const data = await response.json();
      if (data?.address) {
        const ort = data.address.village || data.address.town || data.address.city || 'Unbekannt';
        const plz = data.address.postcode || 'Unbekannt';
        const kanton = data.address.state || 'Unbekannt';
        setCurrentPlatz((prevPlatz) => ({
          ...prevPlatz,
          ort,
          plz,
          kanton,
        }));
      }
    } catch (err) {
      console.error('Fehler beim Geocoding:', err);
    }
  }, []);

  const gefilterteLagerplaetze = useMemo(
    () =>
      lagerplaetze.filter(
        (platz) =>
          platz.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          platz.ort?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          platz.plz?.toString().includes(searchTerm.toLowerCase())
      ),
    [lagerplaetze, searchTerm]
  );

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Lädt...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Fehler</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            Neu laden
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="p-0">
      <style type="text/css">{customStyles}</style>

      {/* Mobile Header */}
      <div className="mobile-header d-md-none">
        <div className="d-flex justify-content-between align-items-center">
          <h2>Lagerplatz</h2>
          <div className="mobile-actions">
            <div className="mobile-search-wrapper">
              <div className={`mobile-search-container ${showSearch ? 'expanded' : 'collapsed'}`}>
                <input
                  type="text"
                  className="mobile-search-input"
                  placeholder="Suche..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              <button
                className={`btn-icon ${showSearch ? 'active' : ''}`}
                onClick={() => setShowSearch((prev) => !prev)}
                aria-label="Suche"
              >
                {showSearch ? <XIcon size={20} /> : <Search size={20} />}
              </button>
            </div>
            <Button className="btn-new-lager" onClick={handleShowModal}>
              <Plus size={16} />
              Neu
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="d-none d-md-block py-4">
        <h2
          className="text-center mb-4"
          style={{ color: '#333', fontWeight: '600', fontSize: '2.4rem', letterSpacing: '0.5px' }}
        >
          Lagerplatzverzeichnis
        </h2>
        <div className="d-flex flex-column flex-md-row gap-2 mb-4 align-items-stretch px-3">
          <InputGroup className="flex-grow-1 custom-search-input">
            <InputGroup.Text>
              <Search size={16} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Suche nach Namen, PLZ oder Ort..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </InputGroup>
          <Button className="btn-new-lager" onClick={handleShowModal}>
            <Plus size={16} />
            Neuer Lagerplatz
          </Button>
        </div>
      </div>

      {/* Cards für gefilterte Lagerplätze */}
      <Row xs={1} sm={2} md={2} lg={3} className="g-3 px-3">
        {gefilterteLagerplaetze.map((platz) => (
          <LagerCard key={platz.id} platz={platz} onEdit={handleEdit} onDelete={handleDelete} />
        ))}
      </Row>

      {/* Modal: Neuer / Bearbeiteter Lagerplatz */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Lagerplatz bearbeiten' : 'Neuer Lagerplatz'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Google Maps Link</Form.Label>
              <Form.Control
                type="url"
                placeholder="Füge hier den Google Maps Link ein"
                value={currentPlatz.googleMapsLink || ''}
                onChange={handleGoogleMapsLinkChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Name des Lagerplatzes</Form.Label>
              <Form.Control
                type="text"
                value={currentPlatz.name}
                onChange={(e) => handleTextFieldChange(e, 'name')}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Vermieter</Form.Label>
              <Row>
                <Col>
                  <Form.Control
                    type="text"
                    placeholder="Vorname"
                    value={currentPlatz.vermieter.vorname}
                    onChange={(e) => handleVermieterFieldChange(e, 'vorname')}
                  />
                </Col>
                <Col>
                  <Form.Control
                    type="text"
                    placeholder="Nachname"
                    value={currentPlatz.vermieter.nachname}
                    onChange={(e) => handleVermieterFieldChange(e, 'nachname')}
                  />
                </Col>
              </Row>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Vermieter Email"
                value={currentPlatz.vermieter.email}
                onChange={(e) => handleVermieterFieldChange(e, 'email')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Telefon</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Vermieter Telefon"
                value={currentPlatz.vermieter.telefon}
                onChange={(e) => handleVermieterFieldChange(e, 'telefon')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Website</Form.Label>
              <Form.Control
                type="url"
                placeholder="Website des Lagerplatzes"
                value={currentPlatz.website}
                onChange={(e) => handleTextFieldChange(e, 'website')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Umgebung</Form.Label>
              <div className="checkbox-group">
                {[
                  'Strom',
                  'Trinkwasser vor Ort',
                  'WC',
                  'Dusche',
                  'Feuerstelle',
                  'Parkplatz',
                  'Wiese',
                ].map((envOption) => (
                  <Form.Check
                    key={envOption}
                    type="checkbox"
                    label={envOption}
                    value={envOption}
                    onChange={handleCheckboxChange}
                    checked={currentPlatz.umgebung.includes(envOption)}
                    className="mb-2"
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Zusätzliche Informationen</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Weitere Details zur Umgebung"
                value={currentPlatz.umgebungText}
                onChange={(e) => handleTextFieldChange(e, 'umgebungText')}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {isEditing ? 'Aktualisieren' : 'Speichern'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="bottom-right" />
    </Container>
  );
}

export default Lagerplatzverzeichnis;